// src/quiz/gre-mock8/quantitativeData.js
// GRE Mock 8 — Quantitative Reasoning.
// Section 1: 12 questions (21 min). Section 2: 15 questions (26 min).
// Question format: see ../greQuestionTypes.js
import { QC_OPTIONS } from "../greQuestionTypes";

const QC_QUESTION = "Compare Quantity A and Quantity B, using additional information centered above the two quantities if such information is given.";
const [QC_A, QC_B, QC_EQUAL, QC_UNKNOWN] = QC_OPTIONS;

const SALES_TABLE = {
  caption: "Annual Sales by Branch (in thousands of dollars)",
  headers: ["Branch", "2021", "2022", "2023", "2024"],
  rows: [
    ["North", "120", "150", "180", "210"],
    ["South", "200", "190", "210", "230"],
    ["East", "90", "110", "100", "140"],
  ],
};

// ================= SECTION 1 =================

export const section1Questions = [
  {
    id: 1,
    question: QC_QUESTION,
    given: "x > 0",
    quantityA: "x²",
    quantityB: "x",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "Try values: x = 2 gives 4 > 2 (A greater), x = 1/2 gives 1/4 < 1/2 (B greater), and x = 1 makes them equal. The relationship depends on x.",
  },
  {
    id: 2,
    question: QC_QUESTION,
    given: "The price of a jacket is raised by 20 percent, and then the new price is lowered by 20 percent.",
    quantityA: "The final price of the jacket",
    quantityB: "The original price of the jacket",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "Final price = original × 1.2 × 0.8 = 0.96 × original. The 20% decrease is taken from a larger base, so the final price is 4% below the original.",
  },
  {
    id: 3,
    question: QC_QUESTION,
    given: "In the xy-plane, line ℓ passes through the points (0, 3) and (4, 11).",
    quantityA: "The slope of line ℓ",
    quantityB: "The y-intercept of line ℓ",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "Slope = (11 − 3)/(4 − 0) = 2. The line passes through (0, 3), so the y-intercept is 3. Since 3 > 2, Quantity B is greater.",
  },
  {
    id: 4,
    question: QC_QUESTION,
    given: "n is an integer greater than 1 and n is divisible by 6.",
    quantityA: "The remainder when n is divided by 4",
    quantityB: "2",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "n = 6 leaves remainder 2 (equal), but n = 12 leaves remainder 0 (B greater). Different multiples of 6 give different results.",
  },
  {
    id: 5,
    question: QC_QUESTION,
    given: "The average (arithmetic mean) of 5 numbers is 20. One of the numbers is removed, and the average of the remaining 4 numbers is 18.",
    quantityA: "The number that was removed",
    quantityB: "28",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "The 5 numbers sum to 5 × 20 = 100; the remaining 4 sum to 4 × 18 = 72. The removed number is 100 − 72 = 28.",
  },
  {
    id: 6,
    question: "If 3x − 7 = 2x + 5, what is the value of 4x?",
    options: ["12", "24", "36", "48", "60"],
    answer: "48",
    explanation: "Subtract 2x and add 7 to both sides: x = 12. Then 4x = 48. (12 is the trap answer—it is x, not 4x.)",
  },
  {
    id: 7,
    question: "A recipe uses sugar and flour in the ratio of 2 to 5 by weight. How many grams of sugar are needed for a mixture that contains 350 grams of flour?",
    options: ["100", "120", "140", "175", "245"],
    answer: "140",
    explanation: "Each ratio unit of flour is 350 ÷ 5 = 70 grams, so sugar is 2 × 70 = 140 grams.",
  },
  {
    id: 8,
    question: "A right triangle has legs of length 9 and 12. What is the length of its hypotenuse?",
    options: ["13", "14", "15", "16", "21"],
    answer: "15",
    explanation: "√(9² + 12²) = √(81 + 144) = √225 = 15. This is the 3-4-5 triangle scaled by 3.",
  },
  {
    id: 9,
    question: "A fair coin is tossed 3 times. What is the probability that exactly two of the tosses result in heads?",
    options: ["1/8", "1/4", "3/8", "1/2", "5/8"],
    answer: "3/8",
    explanation: "There are 2³ = 8 equally likely outcomes. Exactly two heads: HHT, HTH, THH—3 outcomes. Probability = 3/8.",
  },
  {
    id: 10,
    type: "multi",
    question: "Which of the following numbers are prime?",
    options: ["21", "29", "39", "41", "51"],
    answer: ["29", "41"],
    explanation: "21 = 3 × 7, 39 = 3 × 13 and 51 = 3 × 17 are composite (each has digit sum divisible by 3). 29 and 41 have no divisors other than 1 and themselves.",
  },
  {
    id: 11,
    type: "numeric",
    question: "What is the value of 3/4 of 5/6 of 48?",
    answer: 30,
    explanation: "5/6 of 48 is 40, and 3/4 of 40 is 30.",
  },
  {
    id: 12,
    type: "numeric",
    question: "A bag contains 4 red marbles and 6 blue marbles. Two marbles are drawn at random without replacement. What is the probability that both marbles are red? Enter your answer as a fraction.",
    answer: 2 / 15,
    explanation: "P(first red) = 4/10, then P(second red) = 3/9. Multiply: (4/10)(3/9) = 12/90 = 2/15.",
  },
];

// ================= SECTION 2 =================

export const section2Questions = [
  {
    id: 13,
    question: QC_QUESTION,
    given: "a and b are positive integers, and ab = 36.",
    quantityA: "a + b",
    quantityB: "12",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "a = b = 6 gives a + b = 12 (equal), but a = 1, b = 36 gives 37 (A greater). Since both cases are possible, the relationship cannot be determined.",
  },
  {
    id: 14,
    question: QC_QUESTION,
    given: "0 < y < 1",
    quantityA: "y³",
    quantityB: "y²",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "y³ = y² × y, and multiplying a positive number by y < 1 makes it smaller. So y³ < y² for every y in the range.",
  },
  {
    id: 15,
    question: QC_QUESTION,
    given: "In a certain class, 60 percent of the students are girls. Of the girls, 25 percent wear glasses, and of the boys, 40 percent wear glasses.",
    quantityA: "The percent of all students in the class who wear glasses",
    quantityB: "30%",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "Weighted average: 0.6 × 25% + 0.4 × 40% = 15% + 16% = 31%, which is greater than 30%.",
  },
  {
    id: 16,
    question: QC_QUESTION,
    given: "A circle has radius 5.",
    quantityA: "The circumference of the circle",
    quantityB: "30",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "Circumference = 2πr = 10π ≈ 31.4, which is greater than 30.",
  },
  {
    id: 17,
    question: QC_QUESTION,
    given: "x > 0",
    quantityA: "(x + 3)² − x²",
    quantityB: "6x + 9",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "(x + 3)² − x² = x² + 6x + 9 − x² = 6x + 9. The quantities are identical for every x.",
  },
  {
    id: 18,
    question: "If f(x) = 2x² − 3x + 4, what is the value of f(−2)?",
    options: ["6", "10", "14", "18", "22"],
    answer: "18",
    explanation: "f(−2) = 2(4) − 3(−2) + 4 = 8 + 6 + 4 = 18.",
  },
  {
    id: 19,
    question: "A car travels 150 miles in 2.5 hours. At the same average speed, how many hours would it take the car to travel 210 miles?",
    options: ["3", "3.25", "3.5", "3.75", "4"],
    answer: "3.5",
    explanation: "Speed = 150 ÷ 2.5 = 60 miles per hour. Time = 210 ÷ 60 = 3.5 hours.",
  },
  {
    id: 20,
    question: "The sum of three consecutive even integers is 78. What is the largest of the three integers?",
    options: ["24", "26", "28", "30", "32"],
    answer: "28",
    explanation: "The middle integer is the average: 78 ÷ 3 = 26. The three integers are 24, 26 and 28, so the largest is 28.",
  },
  {
    id: 21,
    question: "A rectangular box has length 6, width 4, and height 3. What is the length of the longest line segment that can be drawn between two points of the box?",
    options: ["√45", "√52", "√61", "9", "13"],
    answer: "√61",
    explanation: "The longest segment is the space diagonal: √(6² + 4² + 3²) = √(36 + 16 + 9) = √61.",
  },
  {
    id: 22,
    question: "In how many different ways can a president and a vice president be chosen from a club of 7 members if no member may hold both positions?",
    options: ["13", "21", "35", "42", "49"],
    answer: "42",
    explanation: "Order matters: 7 choices for president, then 6 for vice president, giving 7 × 6 = 42. (21 counts unordered pairs; 49 allows one person in both roles.)",
  },
  {
    id: 23,
    type: "multi",
    question: "If x is an integer and 2 < x² < 30, which of the following could be the value of x?",
    options: ["−6", "−5", "−2", "1", "3", "5"],
    answer: ["−5", "−2", "3", "5"],
    explanation: "Squares: 36, 25, 4, 1, 9, 25. Only 25, 4, 9 and 25 lie strictly between 2 and 30, so x can be −5, −2, 3 or 5.",
  },
  {
    id: 24,
    type: "multi",
    question: "Which of the following inequalities are true for every real number x?",
    options: ["x² ≥ 0", "x² > x", "|x| ≥ x", "x + 1 > x", "2x > x"],
    answer: ["x² ≥ 0", "|x| ≥ x", "x + 1 > x"],
    explanation: "Squares are never negative; |x| equals x or exceeds it; adding 1 always increases a number. But x² > x fails for x = 1/2, and 2x > x fails for x ≤ 0.",
  },
  {
    id: 25,
    type: "numeric",
    question: "If 5x + 3 = 38 − 2x, what is the value of x?",
    answer: 5,
    explanation: "Add 2x and subtract 3 from both sides: 7x = 35, so x = 5.",
  },
  {
    id: 26,
    table: SALES_TABLE,
    question: "Questions 26 and 27 are based on the table above. In which year did the North branch account for the greatest percent of the total sales of the three branches?",
    options: ["2021", "2022", "2023", "2024"],
    answer: "2023",
    explanation: "Totals: 410, 450, 490, 580. North's share: 120/410 ≈ 29.3%, 150/450 ≈ 33.3%, 180/490 ≈ 36.7%, 210/580 ≈ 36.2%. The highest share is in 2023, even though North's sales peaked in 2024.",
  },
  {
    id: 27,
    type: "numeric",
    table: SALES_TABLE,
    question: "What was the percent increase in the total sales of the three branches from 2021 to 2024? Round your answer to the nearest whole percent (enter the number only).",
    answer: 41,
    explanation: "Total 2021 = 120 + 200 + 90 = 410; total 2024 = 210 + 230 + 140 = 580. Increase = 170/410 ≈ 41.46%, which rounds to 41%.",
  },
];
